import { inject, Injectable } from '@angular/core';
import {
  convertGalaxyFilters,
  ContactService,
  CompanyService,
  CustomObjectService,
  ActivityObjectService,
  CRMSelectAllOptions,
  ObjectType,
  TranslateForCrmObjectService,
  SNACKBAR_DURATION,
} from '@galaxy/crm/static';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ObjectSearch } from '@vendasta/bulk-actions';
import { ConfirmationModalComponent, ConfirmationModalConfig } from '@vendasta/galaxy/confirmation-modal';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { switchMap, take, filter, throwError, firstValueFrom } from 'rxjs';
import { BulkDeleteCrmObjectsRequest, FilterGroupInterface } from '@vendasta/crm';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class BulkDeleteService {
  private readonly snackbarService = inject(SnackbarService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly contactService = inject(ContactService);
  private readonly companyService = inject(CompanyService);
  private readonly activityObjectService = inject(ActivityObjectService);
  private readonly customObjectService = inject(CustomObjectService);
  private readonly translateService = inject(TranslateService);
  private readonly translateForCrmObjectService = inject(TranslateForCrmObjectService);

  private getCustomObjectSubtype(): string {
    const url = this.router.url;
    const customObjectTypeFormat = /(CustomObjectTypeID-[-0-9a-zA-Z]*)/;
    const customObjectType = customObjectTypeFormat.exec(url);
    return customObjectType && customObjectType.length > 1 ? customObjectType[1] : '';
  }

  private callService(
    namespace?: string,
    objectType?: ObjectType,
    search?: ObjectSearch,
    filters?: FilterGroupInterface,
    crmObjectSubtype?: string,
  ) {
    switch (objectType as ObjectType) {
      case 'Contact':
        return this.contactService.bulkDelete({ namespace, search, filters } as BulkDeleteCrmObjectsRequest);
      case 'Company':
        return this.companyService.bulkDelete({ namespace, search, filters } as BulkDeleteCrmObjectsRequest);
      case 'Activity':
        return this.activityObjectService.bulkDelete({ namespace, search, filters } as BulkDeleteCrmObjectsRequest);
      case 'CustomObject':
        return this.customObjectService.bulkDelete({
          namespace,
          search,
          filters,
          crmObjectSubtype,
        } as BulkDeleteCrmObjectsRequest);
      default:
        return throwError(() => new Error(`Unknown objectType: ${objectType}`));
    }
  }

  async bulkDeleteDialog(namespace: string, objectType: ObjectType, options?: CRMSelectAllOptions): Promise<void> {
    const titleTranslation = await firstValueFrom(
      this.translateForCrmObjectService.getTranslationForCrmObject(objectType, 'BULK_DELETE.TITLE'),
    );
    const messageTranslation = await firstValueFrom(
      this.translateForCrmObjectService.getTranslationForCrmObject(objectType, 'BULK_DELETE.CONFIRMATION_MESSAGE', {
        amount: options?.totalObjects,
      }),
    );

    this.dialog
      .open(ConfirmationModalComponent, {
        data: {
          type: 'warn',
          title: titleTranslation,
          message:
            messageTranslation + '\n' + this.translateService.instant('LIST_OBJECTS_TABLE.BULK_DELETE.INSTRUCTIONS'),
          confirmationText: this.translateService.instant('LIST_OBJECTS_TABLE.BULK_DELETE.CONFIRMATION_TEXT'),
        } as ConfirmationModalConfig,
        width: '600px',
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed === true),
        switchMap(() => {
          const search = { searchTerm: options?.search || '' } as ObjectSearch;
          const filters = convertGalaxyFilters(options?.filters || []);
          const crmObjectSubtype = this.getCustomObjectSubtype();
          return this.callService(namespace, objectType, search, filters, crmObjectSubtype);
        }),
        take(1),
      )
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessSnack(
            this.translateService.instant('COMMON.SNACKBAR_MESSAGE.BULK_DELETE_SUCCESS', {
              count: options?.totalObjects,
            }),
            { duration: SNACKBAR_DURATION },
          );
        },
        error: (err) => {
          console.error('Bulk delete failed:', err);
          this.snackbarService.openErrorSnack(
            this.translateService.instant('COMMON.SNACKBAR_MESSAGE.BULK_DELETE_FAILURE'),
            { duration: SNACKBAR_DURATION },
          );
        },
      });
  }
}
